import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Switch, Route } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toFullName } from '../../lib/users';
import routes from '../../routes';
import { invitationTypePropType, shareableLinkInfoPropType } from '../prop-types';
import AnswerRequestForm from './Form';
import PhoneValidation from './PhoneValidation';

export default function ShareableLinkAnswerRequest({
  countryCode,
  invitationInfo,
  invitationType,
  onSignIn,
  onSubmit,
  onSubmitWithAuth,
  phoneNumber,
}) {
  const { t } = useTranslation();
  const inviterName = toFullName(invitationInfo.inviterData);

  const renderContactAddedRoute = useCallback(
    props => (
      <PhoneValidation
        inviterName={inviterName}
        onSignIn={onSignIn}
        onSubmitWithAuth={onSubmitWithAuth}
        phoneNumber={phoneNumber}
        {...props}
      />
    ),
    [inviterName, onSignIn, onSubmitWithAuth, phoneNumber],
  );

  const renderRequiresAuthenticationRoute = useCallback(
    props => (
      <PhoneValidation
        inviterName={inviterName}
        onSignIn={onSignIn}
        onSubmitWithAuth={onSubmitWithAuth}
        phoneNumber={phoneNumber}
        mandatory
        {...props}
      />
    ),
    [inviterName, onSignIn, onSubmitWithAuth, phoneNumber],
  );

  const renderForm = useCallback(
    () => (
      <AnswerRequestForm
        initialValues={{ countryCode }}
        invitationType={invitationType}
        inviterName={inviterName}
        onSubmit={onSubmit}
        title={t('answerRequest.shareableLinks.title', { inviterName })}
      />
    ),
    [countryCode, invitationType, inviterName, onSubmit, t],
  );

  return (
    <Switch>
      <Route
        path={routes.answerRequest.contactAdded}
        render={renderContactAddedRoute}
      />
      <Route
        path={routes.answerRequest.requiresAuthentication}
        render={renderRequiresAuthenticationRoute}
      />
      <Route render={renderForm} />
    </Switch>
  );
}

ShareableLinkAnswerRequest.propTypes = {
  countryCode: PropTypes.string.isRequired,
  invitationInfo: shareableLinkInfoPropType.isRequired,
  invitationType: invitationTypePropType.isRequired,
  onSignIn: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onSubmitWithAuth: PropTypes.func.isRequired,
  phoneNumber: PropTypes.string,
};

ShareableLinkAnswerRequest.defaultProps = {
  phoneNumber: undefined,
};
